// @flow

import moment from 'moment';
import getShiftTags from 'sling-calendar-sdk/model-extensions/getShiftTags';

import { extendTimesheetEntry } from '@kiosk/timeClock/utils/extendTimesheetEntry';

import type { Shift, TimesheetProjection, SdkContext } from '../../common/typings';
import {
  getRealDate,
  getDisplayDuration,
  formatTime,
  dateFromHours,
  formatShortTime
} from '../../utils/Utils';

import extendTimesheetProjection from './extendTimesheetProjection';

export default function extendShiftModel(shift: Object, context: SdkContext): Shift {
  const result = { ...shift };
  const { shiftBreakType, usTimeFormat, tags } = context;

  if (typeof shift.dtstart === 'string') {
    result.dtstart = getRealDate(shift.dtstart);
    result.dtstartLocal = new Date(shift.dtstart);
    result.dtstartTimezone = shift.dtstart.substr(19);
  }

  if (typeof shift.dtend === 'string') {
    result.dtend = getRealDate(shift.dtend);
    result.dtendLocal = new Date(shift.dtend);
    result.dtendTimezone = shift.dtend.substr(19);
  }

  if (!('duration' in result) && result.dtend) {
    result.duration = (result.dtend - result.dtstart) / 1000 / 60 / 60; // in HOURS
  }

  const timeFormat = usTimeFormat ? 'h:mm a' : 'H:mm';

  if (result.openEnd) {
    result.displayLabel = `Starts at ${formatShortTime(result.dtstart, usTimeFormat)}`;
  } else if (result.dtend) {
    result.displayLabel = `${moment(result.dtstart).format(timeFormat)} - ${moment(
      result.dtend
    ).format(timeFormat)}  •  ${getDisplayDuration(result.duration * 60, usTimeFormat)}`;
  } else {
    result.displayLabel = `Starts at ${formatShortTime(result.dtstart, usTimeFormat)}`;
  }

  result.breakLabel = getBreakLabel(result, shiftBreakType);

  result.timesheetProjections = (result.timesheetProjections || []).map(extendTimesheetProjection);
  const firstProjection = getFirstProjection(result);
  const lastProjection = getLastProjection(result);

  if ('timesheetEntries' in result) {
    result.timesheetEntries = result.timesheetEntries.map(extendTimesheetEntry);
  }

  result.actualBreakDuration = result.timesheetProjections.reduce(
    (acc, projection) => acc + projection.breakMinutes,
    0
  );

  if (
    lastProjection &&
    (result.timesheetProjections.length > 1 ||
      lastProjection.lastAction.getTime() !== lastProjection.clockIn.getTime())
  ) {
    result.clockedBackIn = lastProjection.lastAction;
  }

  if (firstProjection && result.user && firstProjection.user.id === result.user.id) {
    result.clockedIn = firstProjection.clockIn;
    result.clockedInLocal = firstProjection.clockInLocal;
    result.clockInTime = dateFromHours(formatTime(result.clockedIn, false, true));
  }

  if (
    result.user &&
    lastProjection &&
    lastProjection.user.id === result.user.id &&
    lastProjection.clockOut
  ) {
    result.clockedOut = lastProjection.clockOut;
    result.clockedOutLocal = lastProjection.clockOutLocal;

    if (result.clockedOut) {
      result.clockOutTime = dateFromHours(formatTime(result.clockedOut, false, true));
      result.actualDuration = result.timesheetProjections.reduce(
        (acc, projection) => acc + projection.paidMinutes,
        0
      );
      result.actualDifference = result.actualDuration - result.paidDurationMinutes;
    }
  }

  result.clockInStatus = {
    get clockedIn() {
      return lastProjection && !lastProjection.clockOut;
    },
    get eligibleForBreak() {
      return !!shiftBreakType;
    },
    get onBreak() {
      return lastProjection && lastProjection.onBreak;
    },
    get clockedOut() {
      return lastProjection && Boolean(lastProjection.clockOut);
    },
    get lastProjection() {
      return lastProjection;
    }
  };

  result.tags = getShiftTags(result, tags);

  return result;
}

function getFirstProjection(shift: Shift): TimesheetProjection | null {
  return (shift.timesheetProjections.length && shift.timesheetProjections[0]) || null;
}

function getLastProjection(shift: Shift): TimesheetProjection | null {
  return (
    (shift.timesheetProjections.length &&
      shift.timesheetProjections[shift.timesheetProjections.length - 1]) ||
    null
  );
}

function getBreakLabel(shift: Shift, shiftBreakType: boolean): string {
  return shiftBreakType && shift.breakDuration ? `${shift.breakDuration} min break` : '';
}
